/**
 * Stock In — full purchase order lifecycle.
 *
 * Sections:
 *  1. Pending Orders  — draft + sent POs, with WhatsApp send (undo toast) and Receive flow
 *  2. Generate Orders — auto-create draft POs from low-stock ingredients
 *  3. New Purchase    — manual stock-in (creates a 'received' PO immediately)
 *  4. History         — paginated list of received POs
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmd, resolveAppLocale } from '../locale.js';
import appI18n from '../i18n.js';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { LowStockPanel } from '../components/LowStockPanel.js';
import { Plus, X, Zap } from 'lucide-react';
import {
  Button,
  Callout,
  DateRange,
  Field,
  FormGrid,
  IconButton,
  Modal,
  PageHeader,
  resolveDateRange,
  type DateRangeValue,
} from '../ui/index.js';

type IngredientRow = Database['public']['Tables']['ingredients']['Row'];
type PurchaseOrderRow = Database['public']['Tables']['purchase_orders']['Row'];
type PurchaseOrderItemRow = Database['public']['Tables']['purchase_order_items']['Row'];

interface SupplierInfo {
  id: string;
  display_name: string;
  phone: string | null;
  tin: string;
}

interface PurchaseOrderWithItems extends PurchaseOrderRow {
  items: (PurchaseOrderItemRow & { ingredient_name: string; unit: string })[];
  supplierInfo: SupplierInfo | null;
}

/** Restock with weighted-average cost (see migration 0076). */
async function applyStockInLine(
  clientId: string,
  ingredientId: string,
  qty: number,
  unitCost: number,
  unit: string,
  tx: { reference_id: string; note: string },
): Promise<string | null> {
  if (!supabase) return appI18n.t('stockIn.loadFailed');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: rpcErr } = await (supabase as any).rpc('restock_ingredient_wac', {
    p_ingredient_id: ingredientId,
    p_delta: qty,
    p_purchase_unit_cost: unitCost > 0 ? unitCost : null,
  });
  if (rpcErr) return rpcErr.message;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: txErr } = await (supabase as any).from('inventory_transactions').insert({
    client_id: clientId,
    ingredient_id: ingredientId,
    delta: qty,
    type: 'restock' as const,
    reference_id: tx.reference_id,
    note: tx.note,
    unit_cost: unitCost > 0 ? unitCost : null,
    unit: unit || null,
  });
  return txErr?.message ?? null;
}

interface LineItem {
  ingredient_id: string;
  search: string;
  quantity: string;
  unit_cost: string;
}

interface GenerateLine {
  ingredient_id: string;
  name: string;
  unit: string;
  current_stock: number;
  threshold: number;
  supplier_id: string;
  quantity: string;
}

const BLANK_LINE: LineItem = { ingredient_id: '', search: '', quantity: '', unit_cost: '' };
const PAGE_SIZE = 25;
const AMD = (n: number) => `${Number(n).toLocaleString('en-US')} ֏`;
const fmt = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });

function poInDateRange(po: PurchaseOrderWithItems, from: string, to: string): boolean {
  const ts = new Date(po.created_at).getTime();
  if (from) {
    const start = new Date(`${from}T00:00:00`).getTime();
    if (ts < start) return false;
  }
  if (to) {
    const end = new Date(`${to}T23:59:59.999`).getTime();
    if (ts > end) return false;
  }
  return true;
}

function buildWhatsAppText(
  po: PurchaseOrderWithItems,
  ingredients: IngredientRow[],
): string {
  const ingById = Object.fromEntries(ingredients.map((i) => [i.id, i]));
  const lines = po.items
    .map((item) => {
      const unit = ingById[item.ingredient_id]?.unit ?? item.unit;
      return `• ${item.ingredient_name}: ${item.quantity} ${unit}`;
    })
    .join('\n');
  const date = new Date().toLocaleDateString('en-GB');
  return `Order request — ${date}\n\n${lines}`;
}

export function StockInView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [prepIngredientIds, setPrepIngredientIds] = useState<Set<string>>(new Set());
  const [suppliers, setSuppliers] = useState<SupplierInfo[]>([]);
  const [orders, setOrders] = useState<PurchaseOrderWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [historyRange, setHistoryRange] = useState<DateRangeValue>({ preset: 'this_month' });
  const { from: historyDateFrom = '', to: historyDateTo = '' } = resolveDateRange(historyRange);

  // Manual stock-in form
  const [showForm, setShowForm] = useState(false);
  const [receivingPoId, setReceivingPoId] = useState<string | null>(null);
  const [formSupplierId, setFormSupplierId] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<LineItem[]>([{ ...BLANK_LINE }]);

  // Delete / undo received PO
  const [confirmDelete, setConfirmDelete] = useState<PurchaseOrderWithItems | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Cancel pending (draft/sent) PO
  const [confirmCancel, setConfirmCancel] = useState<PurchaseOrderWithItems | null>(null);

  // Generate orders modal
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateLines, setGenerateLines] = useState<GenerateLine[]>([]);
  const [generating, setGenerating] = useState(false);

  // Undo toast for WhatsApp send
  const [undoToast, setUndoToast] = useState<{ poId: string } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // ─── Data loading ───────────────────────────────────────────────────────────

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);

    const [ingRes, suppRes, poRes, poItemsRes, prepRes] = await Promise.all([
      supabase.from('ingredients').select('*').order('name'),
      supabase
        .from('suppliers')
        .select('id, display_name, phone, tin')
        .order('display_name'),
      supabase.from('purchase_orders').select('*').order('created_at', { ascending: false }),
      supabase.from('purchase_order_items').select('*'),
      supabase
        .from('recipes')
        .select('prep_ingredient_id')
        .eq('is_prep', true)
        .not('prep_ingredient_id', 'is', null),
    ]);

    if (ingRes.error || poRes.error || poItemsRes.error) {
      setError(
        ingRes.error?.message ?? poRes.error?.message ?? poItemsRes.error?.message ?? t('stockIn.loadFailed'),
      );
      setLoading(false);
      return;
    }

    setIngredients(ingRes.data);
    setSuppliers((suppRes.data ?? []) as SupplierInfo[]);
    setPrepIngredientIds(
      new Set(
        ((prepRes.data ?? []) as { prep_ingredient_id: string }[]).map((r) => r.prep_ingredient_id),
      ),
    );

    const ingById = Object.fromEntries(ingRes.data.map((i) => [i.id, i]));
    const supplierById = Object.fromEntries(
      ((suppRes.data ?? []) as SupplierInfo[]).map((s) => [s.id, s]),
    );
    const itemsByOrder: Record<string, PurchaseOrderItemRow[]> = {};
    (poItemsRes.data as PurchaseOrderItemRow[]).forEach((item) => {
      (itemsByOrder[item.purchase_order_id] ||= []).push(item);
    });

    const enriched: PurchaseOrderWithItems[] = (poRes.data as PurchaseOrderRow[]).map((po) => ({
      ...po,
      supplierInfo: po.supplier_id ? (supplierById[po.supplier_id] ?? null) : null,
      items: (itemsByOrder[po.id] ?? []).map((item) => ({
        ...item,
        ingredient_name: ingById[item.ingredient_id]?.name ?? t('common.unknown'),
        unit: ingById[item.ingredient_id]?.unit ?? '',
      })),
    }));

    setOrders(enriched);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [clientId]);

  useEffect(() => {
    setPage(0);
  }, [historyRange]);

  // ─── Manual stock-in form helpers ───────────────────────────────────────────

  function resetForm() {
    setNote('');
    setFormSupplierId('');
    setLines([{ ...BLANK_LINE }]);
    setShowForm(false);
    setReceivingPoId(null);
    setError(null);
  }

  function openNewForm() {
    setReceivingPoId(null);
    setNote('');
    setFormSupplierId('');
    setLines([{ ...BLANK_LINE }]);
    setShowForm(true);
  }

  /** Pre-fill the form with a pending PO's items so the cashier can confirm actuals. */
  function openReceiveForm(po: PurchaseOrderWithItems) {
    setReceivingPoId(po.id);
    setNote(po.note ?? '');
    setFormSupplierId(po.supplier_id ?? '');
    setLines(
      po.items.map((item) => ({
        ingredient_id: item.ingredient_id,
        search: item.ingredient_name,
        quantity: String(item.quantity),
        unit_cost: item.unit_cost > 0 ? String(item.unit_cost) : '',
      })),
    );
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function addLine() {
    setLines((prev) => [...prev, { ...BLANK_LINE }]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateLine(idx: number, field: keyof LineItem, value: string) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  }

  async function submit() {
    if (!supabase || !clientId) return;

    const validLines = lines.filter((l) => l.ingredient_id && Number(l.quantity) > 0);
    if (validLines.length === 0) {
      setError(t('stockIn.noLines'));
      return;
    }
    const ids = validLines.map((l) => l.ingredient_id);
    if (new Set(ids).size !== ids.length) {
      setError(t('stockIn.duplicateIngredient'));
      return;
    }

    setSaving(true);
    setError(null);

    const { data: userData } = await supabase.auth.getUser();
    const u = userData?.user;
    const by = u?.user_metadata?.full_name || u?.email || u?.id || 'unknown';

    if (receivingPoId) {
      // Receiving against an existing draft/sent PO — update items + mark received
      await supabase
        .from('purchase_order_items')
        .delete()
        .eq('purchase_order_id', receivingPoId);

      await supabase.from('purchase_order_items').insert(
        validLines.map((l) => ({
          purchase_order_id: receivingPoId,
          client_id: clientId,
          ingredient_id: l.ingredient_id,
          quantity: Number(l.quantity),
          unit_cost: Number(l.unit_cost) || 0,
        })),
      );

      const { error: poErr } = await supabase
        .from('purchase_orders')
        .update({
          status: 'received',
          note: note.trim() || null,
          supplier_id: formSupplierId || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', receivingPoId);

      if (poErr) {
        setError(poErr.message);
        setSaving(false);
        return;
      }

      // Update stock + inventory transactions (weighted-average cost when unit price entered)
      for (const l of validLines) {
        const qty = Number(l.quantity);
        const unitCost = Number(l.unit_cost) || 0;
        const ing = ingredients.find((i) => i.id === l.ingredient_id);
        if (!ing) continue;
        const err = await applyStockInLine(clientId, ing.id, qty, unitCost, ing.unit, {
          reference_id: receivingPoId,
          note: `Stock-in delivery${note.trim() ? `: ${note.trim()}` : ''}`,
        });
        if (err) {
          setError(err);
          setSaving(false);
          return;
        }
      }
    } else {
      // New manual stock-in — create PO with status='received'
      const { data: po, error: poErr } = await supabase
        .from('purchase_orders')
        .insert({
          client_id: clientId,
          supplier_id: formSupplierId || null,
          status: 'received' as const,
          note: note.trim() || null,
          created_by: by,
        })
        .select('id')
        .single();

      if (poErr || !po) {
        setError(poErr?.message ?? t('stockIn.createFailed'));
        setSaving(false);
        return;
      }

      const { error: itemsErr } = await supabase.from('purchase_order_items').insert(
        validLines.map((l) => ({
          purchase_order_id: po.id,
          client_id: clientId,
          ingredient_id: l.ingredient_id,
          quantity: Number(l.quantity),
          unit_cost: Number(l.unit_cost) || 0,
        })),
      );
      if (itemsErr) {
        setError(itemsErr.message);
        setSaving(false);
        return;
      }

      for (const l of validLines) {
        const qty = Number(l.quantity);
        const unitCost = Number(l.unit_cost) || 0;
        const ing = ingredients.find((i) => i.id === l.ingredient_id);
        if (!ing) continue;
        const err = await applyStockInLine(clientId, ing.id, qty, unitCost, ing.unit, {
          reference_id: po.id,
          note: `Stock-in delivery${note.trim() ? `: ${note.trim()}` : ''}`,
        });
        if (err) {
          setError(err);
          setSaving(false);
          return;
        }
      }
    }

    setSaving(false);
    resetForm();
    void load();
  }

  // ─── Pending PO actions ──────────────────────────────────────────────────────

  /** Optimistically mark PO as 'sent', open WhatsApp, show undo toast. */
  async function sendViaWhatsApp(po: PurchaseOrderWithItems) {
    if (!supabase) return;

    const text = buildWhatsAppText(po, ingredients);
    const phone = (po.supplierInfo?.phone ?? '').replace(/[^0-9+]/g, '');
    if (phone) {
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
    }

    // Optimistic update
    setOrders((prev) => prev.map((o) => (o.id === po.id ? { ...o, status: 'sent' } : o)));
    await supabase
      .from('purchase_orders')
      .update({ status: 'sent', updated_at: new Date().toISOString() })
      .eq('id', po.id);

    // Show undo toast
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoToast({ poId: po.id });
    undoTimerRef.current = setTimeout(() => setUndoToast(null), 7000);
  }

  async function undoSend(poId: string) {
    if (!supabase) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoToast(null);
    setOrders((prev) => prev.map((o) => (o.id === poId ? { ...o, status: 'draft' } : o)));
    await supabase
      .from('purchase_orders')
      .update({ status: 'draft', updated_at: new Date().toISOString() })
      .eq('id', poId);
  }

  async function cancelPendingOrder(po: PurchaseOrderWithItems) {
    if (!supabase) return;
    await supabase
      .from('purchase_orders')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .eq('id', po.id);
    setConfirmCancel(null);
    void load();
  }

  // ─── Delete received PO (reverts stock) ─────────────────────────────────────

  async function deleteReceivedPO(po: PurchaseOrderWithItems) {
    if (!supabase) return;
    setDeleting(po.id);
    for (const item of po.items) {
      const ing = ingredients.find((i) => i.id === item.ingredient_id);
      if (ing) {
        await supabase
          .from('ingredients')
          .update({ stock: Math.max(0, ing.stock - item.quantity) })
          .eq('id', ing.id);
      }
    }
    await supabase.from('inventory_transactions').delete().eq('reference_id', po.id);
    await supabase.from('purchase_order_items').delete().eq('purchase_order_id', po.id);
    await supabase.from('purchase_orders').delete().eq('id', po.id);
    setDeleting(null);
    setConfirmDelete(null);
    setPage(0);
    void load();
  }

  // ─── Generate orders ─────────────────────────────────────────────────────────

  function openGenerateModal() {
    const lowStock = ingredients.filter(
      (i) => i.low_stock_threshold > 0 && i.stock <= i.low_stock_threshold,
    );
    if (lowStock.length === 0) {
      setError(t('stockIn.noLowStock'));
      return;
    }
    setGenerateLines(
      lowStock.map((i) => ({
        ingredient_id: i.id,
        name: i.name,
        unit: i.unit,
        current_stock: i.stock,
        threshold: i.low_stock_threshold,
        supplier_id: i.preferred_supplier_id ?? '',
        quantity: i.reorder_quantity > 0 ? String(i.reorder_quantity) : '',
      })),
    );
    setShowGenerate(true);
    setError(null);
  }

  async function submitGenerateOrders() {
    if (!supabase || !clientId) return;
    const valid = generateLines.filter((l) => l.supplier_id && Number(l.quantity) > 0);
    if (valid.length === 0) {
      return;
    }
    setGenerating(true);

    const { data: userData } = await supabase.auth.getUser();
    const u = userData?.user;
    const by = u?.user_metadata?.full_name || u?.email || u?.id || 'unknown';

    // Group by supplier
    const bySupplier = new Map<string, GenerateLine[]>();
    valid.forEach((l) => {
      const arr = bySupplier.get(l.supplier_id) ?? [];
      arr.push(l);
      bySupplier.set(l.supplier_id, arr);
    });

    for (const [supplierId, groupLines] of bySupplier) {
      const supplierName =
        suppliers.find((s) => s.id === supplierId)?.display_name ?? t('common.unknown');
      const { data: po, error: poErr } = await supabase
        .from('purchase_orders')
        .insert({
          client_id: clientId,
          supplier_id: supplierId,
          status: 'draft' as const,
          note: supplierName ? `Auto-generated for ${supplierName}` : null,
          created_by: by,
        })
        .select('id')
        .single();

      if (poErr || !po) continue;

      await supabase.from('purchase_order_items').insert(
        groupLines.map((l) => ({
          purchase_order_id: po.id,
          client_id: clientId,
          ingredient_id: l.ingredient_id,
          quantity: Number(l.quantity),
          unit_cost: 0,
        })),
      );

      // Save preferred_supplier_id back to ingredient for future use
      for (const l of groupLines) {
        const ing = ingredients.find((i) => i.id === l.ingredient_id);
        if (ing && !ing.preferred_supplier_id) {
          await supabase
            .from('ingredients')
            .update({ preferred_supplier_id: supplierId })
            .eq('id', l.ingredient_id);
        }
      }
    }

    setGenerating(false);
    setShowGenerate(false);
    void load();
  }

  // ─── Derived ─────────────────────────────────────────────────────────────────

  const pendingOrders = orders.filter((o) => o.status === 'draft' || o.status === 'sent');
  const receivedOrders = orders.filter((o) => o.status === 'received');
  const filteredReceivedOrders = receivedOrders.filter((po) =>
    poInDateRange(po, historyDateFrom, historyDateTo),
  );
  const historyPageCount = Math.max(1, Math.ceil(filteredReceivedOrders.length / PAGE_SIZE));
  const paginatedReceivedOrders = filteredReceivedOrders.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE,
  );
  const lowStockIngredients = ingredients.filter(
    (i) => i.stock < 0 || (i.low_stock_threshold > 0 && i.stock <= i.low_stock_threshold),
  );
  const lowStockCount = lowStockIngredients.length;

  function exportPurchaseHistoryCsv() {
    const escape = (v: string | number) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const headers = [
      t('stockIn.csvDate'),
      t('stockIn.csvSupplier'),
      t('stockIn.csvIngredient'),
      t('stockIn.csvQuantity'),
      t('stockIn.csvUnit'),
      t('stockIn.csvUnitCost'),
      t('stockIn.csvLineTotal'),
      t('stockIn.csvAddedBy'),
      t('stockIn.csvNote'),
    ];
    const rows: string[] = [];
    for (const po of filteredReceivedOrders) {
      const supplier = po.supplierInfo?.display_name ?? '';
      const date = po.created_at
        ? new Date(po.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
        : '';
      const addedBy = po.created_by ?? '';
      const note = po.note ?? '';
      if (po.items.length === 0) {
        rows.push(
          [date, supplier, '', '', '', '', '', addedBy, note].map(escape).join(','),
        );
        continue;
      }
      for (const item of po.items) {
        const lineTotal = item.quantity * item.unit_cost;
        rows.push(
          [
            date,
            supplier,
            item.ingredient_name,
            item.quantity,
            item.unit,
            item.unit_cost,
            lineTotal,
            addedBy,
            note,
          ].map(escape).join(','),
        );
      }
    }
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-history-${historyDateFrom}-to-${historyDateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ─── JSX ─────────────────────────────────────────────────────────────────────

  return (
    <section>
      {/* Undo toast */}
      {undoToast && (
        <div className="undo-toast">
          <span>{t('stockIn.undoToast')}</span>
          <button
            onClick={() => void undoSend(undoToast.poId)}
          >
            {t('common.undo')}
          </button>
        </div>
      )}

      <PageHeader
        title={t('stockIn.title')}
        subtitle={t('stockIn.subtitle')}
        actions={
          <Button variant="primary" icon={<Plus />} onClick={openNewForm}>
            {t('stockIn.newPurchase')}
          </Button>
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}

      <LowStockPanel
        ingredients={ingredients}
        prepIngredientIds={prepIngredientIds}
        action={
          lowStockCount > 0 ? (
            <button className="btn btn-sm" onClick={openGenerateModal}>
              <Zap className="ui-i" /> {t('stockIn.generateOrders')}
            </button>
          ) : undefined
        }
      />

      {/* ── Delete received PO confirmation ── */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => deleting === null && setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('stockIn.undoTitle')}</h2>
            </div>
            <p style={{ padding: '0 4px 12px', color: 'var(--text-muted)' }}>
              {t('stockIn.undoBody')}
            </p>
            <ul style={{ padding: '0 4px 16px 24px', margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
              {confirmDelete.items.map((item) => (
                <li key={item.id}>
                  <strong>{item.ingredient_name}</strong>: −{item.quantity} {item.unit}
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" disabled={deleting !== null} onClick={() => setConfirmDelete(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn"
                style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
                disabled={deleting !== null}
                onClick={() => void deleteReceivedPO(confirmDelete)}
              >
                {deleting !== null ? '…' : t('stockIn.undoConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel pending PO confirmation ── */}
      {confirmCancel && (
        <div className="modal-overlay" onClick={() => setConfirmCancel(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('stockIn.cancelTitle')}</h2>
            </div>
            <p style={{ padding: '0 4px 12px', color: 'var(--text-muted)' }}>
              {t('stockIn.cancelBody')}
            </p>
            {confirmCancel.items.length > 0 && (
              <ul style={{ padding: '0 4px 16px 24px', margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
                {confirmCancel.items.map((item) => (
                  <li key={item.id}>
                    {item.ingredient_name}: {item.quantity} {item.unit}
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" onClick={() => setConfirmCancel(null)}>
                {t('common.keep')}
              </button>
              <button
                className="btn"
                style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
                onClick={() => void cancelPendingOrder(confirmCancel)}
              >
                {t('stockIn.cancelConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Generate Orders modal ── */}
      {showGenerate && (
        <div className="modal-overlay" onClick={() => !generating && setShowGenerate(false)}>
          <div
            className="modal"
            style={{ maxWidth: 680, width: '96vw', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{t('stockIn.generateTitle')}</h2>
              <button className="btn-icon" disabled={generating} onClick={() => setShowGenerate(false)}>×</button>
            </div>
            <p style={{ marginBottom: 16, color: 'var(--text-muted)', fontSize: 13 }}>
              {t('stockIn.generateIntro', { count: generateLines.length })}
            </p>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('stockIn.colIngredient')}</th>
                  <th>{t('stockIn.colInStock')}</th>
                  <th>{t('stockIn.colSupplier')}</th>
                  <th>{t('stockIn.colQtyToOrder')}</th>
                </tr>
              </thead>
              <tbody>
                {generateLines.map((line, idx) => (
                  <tr key={line.ingredient_id}>
                    <td>
                      <span style={{ fontWeight: 500 }}>{line.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>
                        {t('stockIn.thresholdHint', { threshold: line.threshold, unit: line.unit })}
                      </span>
                    </td>
                    <td style={{ color: 'var(--danger)', fontWeight: 600 }}>
                      {line.current_stock} {line.unit}
                    </td>
                    <td>
                      <select
                        value={line.supplier_id}
                        onChange={(e) =>
                          setGenerateLines((prev) =>
                            prev.map((l, i) =>
                              i === idx ? { ...l, supplier_id: e.target.value } : l,
                            ),
                          )
                        }
                        style={{ minWidth: 140 }}
                      >
                        <option value="">{t('stockIn.supplierNone')}</option>
                        {suppliers.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.display_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          className="input"
                          type="number"
                          min="0"
                          step="any"
                          placeholder={t('stockIn.qtyPlaceholder')}
                          value={line.quantity}
                          onChange={(e) =>
                            setGenerateLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, quantity: e.target.value } : l,
                              ),
                            )
                          }
                          style={{ width: 80 }}
                        />
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{line.unit}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {generateLines.filter((l) => l.supplier_id && Number(l.quantity) > 0).length === 0 && (
              <p style={{ padding: '8px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                {t('stockIn.needOne')}
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn-ghost" disabled={generating} onClick={() => setShowGenerate(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-primary"
                disabled={
                  generating ||
                  generateLines.filter((l) => l.supplier_id && Number(l.quantity) > 0).length === 0
                }
                onClick={() => void submitGenerateOrders()}
              >
                {generating ? t('common.creating') : t('stockIn.createDrafts', { count: new Set(generateLines.filter((l) => l.supplier_id && Number(l.quantity) > 0).map((l) => l.supplier_id)).size || 0 })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pending Orders ── */}
      {!loading && pendingOrders.length > 0 && (
        <div className="panel" style={{ marginBottom: 24 }}>
          <div className="panel-header">
            <span className="panel-title">{t('stockIn.pendingTitle')}</span>
            <span className="badge badge-accent">{pendingOrders.length}</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>{t('stockIn.colStatus')}</th>
                <th>{t('stockIn.colSupplier')}</th>
                <th>{t('stockIn.colItems')}</th>
                <th>{t('stockIn.colCreated')}</th>
                <th>{t('stockIn.colNote')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pendingOrders.map((po) => {
                const hasPhone = Boolean(po.supplierInfo?.phone);
                return (
                  <>
                    <tr key={po.id}>
                      <td>
                        <span className={`badge ${po.status === 'sent' ? 'badge-success' : 'badge-muted'}`}>
                          {t(`stockIn.status${po.status.charAt(0).toUpperCase()}${po.status.slice(1)}`, { defaultValue: po.status })}
                        </span>
                      </td>
                      <td style={{ fontWeight: 500 }}>
                        {po.supplierInfo?.display_name ?? (
                          <span style={{ color: 'var(--text-muted)' }}>{t('stockIn.noSupplier')}</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>{po.items.length}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{fmt(po.created_at)}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{po.note ?? '—'}</td>
                      <td>
                        <div className="row-actions">
                          {(po.status === 'draft' || po.status === 'sent') && (
                            <button
                              className="btn btn-sm"
                              style={{ background: '#25d366', borderColor: '#25d366', color: '#fff' }}
                              title={
                                hasPhone
                                  ? t('stockIn.whatsappTitle', { name: po.supplierInfo?.display_name ?? '' })
                                  : t('stockIn.whatsappNoPhone')
                              }
                              disabled={!hasPhone}
                              onClick={() => void sendViaWhatsApp(po)}
                            >
                              {t('stockIn.whatsapp')}
                            </button>
                          )}
                          <button
                            className="btn btn-sm"
                            onClick={() => openReceiveForm(po)}
                            title={t('stockIn.receiveTitle')}
                          >
                            {t('stockIn.receive')}
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            style={{ color: 'var(--text-muted)' }}
                            onClick={() => setConfirmCancel(po)}
                            title={t('stockIn.cancelOrderTitle')}
                          >
                            <X className="ui-i" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {po.items.length > 0 && (
                      <tr key={`${po.id}-items`}>
                        <td colSpan={6} style={{ paddingTop: 0, paddingLeft: 32, paddingBottom: 12 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {po.items.map((item) => (
                              <span
                                key={item.id}
                                style={{
                                  background: 'var(--surface-raised)',
                                  borderRadius: 4,
                                  padding: '2px 8px',
                                  fontSize: 12,
                                  color: 'var(--text-muted)',
                                }}
                              >
                                {item.ingredient_name}: {item.quantity} {item.unit}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── New / Receive form ── */}
      {(() => {
        const totalCost = lines.reduce(
          (sum, l) => sum + Number(l.quantity || 0) * Number(l.unit_cost || 0),
          0,
        );
        const validLines = lines.filter((l) => l.ingredient_id && Number(l.quantity) > 0).length;
        return (
          <Modal
            open={showForm}
            wide
            onClose={() => !saving && resetForm()}
            title={receivingPoId ? t('stockIn.confirmDelivery') : t('stockIn.newPurchase')}
            caption={
              receivingPoId
                ? t('stockIn.captionReceive')
                : t('stockIn.captionNew')
            }
            footerLeft={
              <span className="stockin-total">
                {t('stockIn.footerLines', { count: validLines })}
                {totalCost > 0 && <>{t('stockIn.footerTotal', { amount: AMD(totalCost) })}</>}
              </span>
            }
            footer={
              <>
                <Button variant="ghost" onClick={resetForm} disabled={saving}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={() => void submit()} disabled={saving || validLines === 0}>
                  {saving ? t('common.saving') : receivingPoId ? t('stockIn.confirmReceipt') : t('stockIn.confirmPurchase')}
                </Button>
              </>
            }
          >
            <div className="stockin-form">
              <FormGrid>
                <Field label={t('stockIn.supplier')} htmlFor="stockin-supplier" optional>
                  <select
                    id="stockin-supplier"
                    value={formSupplierId}
                    onChange={(e) => setFormSupplierId(e.target.value)}
                  >
                    <option value="">{t('stockIn.noSupplier')}</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.display_name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('stockIn.note')} htmlFor="stockin-note" optional>
                  <input
                    id="stockin-note"
                    placeholder={t('stockIn.notePlaceholder')}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>
              </FormGrid>

              <datalist id="stock-in-ingredients">
                {ingredients
                  .filter((i) => i.active !== false)
                  .map((i) => (
                    <option key={i.id} value={i.name} />
                  ))}
              </datalist>

              <div className="stockin-lines">
                <div className="stockin-line stockin-line--head">
                  <span>{t('stockIn.colIngredient')}</span>
                  <span>{t('stockIn.colQtyReceived')}</span>
                  <span>{t('stockIn.colUnitCost')}</span>
                  <span />
                </div>
                {lines.map((line, idx) => {
                  const ing = ingredients.find((i) => i.id === line.ingredient_id);
                  const noMatch = Boolean(line.search) && !line.ingredient_id;
                  return (
                    <div key={idx} className="stockin-line">
                      <div className="stockin-cell">
                        <input
                          list="stock-in-ingredients"
                          placeholder={t('stockIn.typeIngredient')}
                          value={line.search}
                          aria-invalid={noMatch || undefined}
                          onChange={(e) => {
                            const val = e.target.value;
                            const match = ingredients.find(
                              (i) => i.active !== false && i.name.toLowerCase() === val.toLowerCase(),
                            );
                            updateLine(idx, 'search', val);
                            setLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, search: val, ingredient_id: match?.id ?? '' } : l,
                              ),
                            );
                          }}
                          onBlur={(e) => {
                            const match = ingredients.find(
                              (i) =>
                                i.active !== false &&
                                i.name.toLowerCase() === e.target.value.toLowerCase(),
                            );
                            if (!match)
                              setLines((prev) =>
                                prev.map((l, i) => (i === idx ? { ...l, search: '', ingredient_id: '' } : l)),
                              );
                          }}
                          autoComplete="off"
                          autoFocus={idx === lines.length - 1 && !line.search && !receivingPoId}
                        />
                        {noMatch ? (
                          <span className="stockin-hint text-danger">{t('stockIn.noMatch')}</span>
                        ) : ing ? (
                          <span className="stockin-hint">
                            {t('stockIn.inStock', { stock: ing.stock, unit: ing.unit })}
                          </span>
                        ) : null}
                      </div>
                      <div className="stockin-cell stockin-cell--qty">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder={t('stockIn.qtyPlaceholder')}
                          value={line.quantity}
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                        />
                        <span className="stockin-unit">{ing?.unit ?? ''}</span>
                      </div>
                      <div className="stockin-cell">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder={t('stockIn.unitCostPlaceholder')}
                          value={line.unit_cost}
                          onChange={(e) => updateLine(idx, 'unit_cost', e.target.value)}
                        />
                      </div>
                      <div className="stockin-cell stockin-cell--actions">
                        <IconButton
                          label={t('stockIn.removeLine')}
                          size="sm"
                          danger
                          onClick={() => removeLine(idx)}
                          disabled={lines.length === 1}
                        >
                          <X />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <Button variant="secondary" size="sm" icon={<Plus />} onClick={addLine}>
                  {t('stockIn.addIngredient')}
                </Button>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Purchase History ── */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">{t('stockIn.historyTitle')}</span>
          {filteredReceivedOrders.length > 0 && (
            <button className="btn btn-sm" type="button" onClick={exportPurchaseHistoryCsv}>
              ↓ {t('common.exportCsv')}
            </button>
          )}
        </div>
        <div
          className="toolbar"
          style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', margin: 0 }}
        >
          <DateRange value={historyRange} onChange={setHistoryRange} size="sm" />
          {filteredReceivedOrders.length > 0 && (
            <span className="muted" style={{ fontSize: 13 }}>
              {t('stockIn.purchaseCount', { count: filteredReceivedOrders.length })}
            </span>
          )}
        </div>
        {loading ? (
          <div style={{ padding: 16, color: 'var(--text-muted)' }}>{t('common.loading')}</div>
        ) : receivedOrders.length === 0 ? (
          <div style={{ padding: 16, color: 'var(--text-muted)' }}>{t('stockIn.empty')}</div>
        ) : filteredReceivedOrders.length === 0 ? (
          <div style={{ padding: 16, color: 'var(--text-muted)' }}>
            {t('stockIn.emptyRange')}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('stockIn.colDate')}</th>
                <th>{t('stockIn.colSupplier')}</th>
                <th>{t('stockIn.colItems')}</th>
                <th>{t('stockIn.colTotalCost')}</th>
                <th>{t('stockIn.colAddedBy')}</th>
                <th>{t('stockIn.colNote')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedReceivedOrders.map((po) => {
                const totalCost = po.items.reduce(
                  (sum, item) => sum + item.quantity * item.unit_cost,
                  0,
                );
                return (
                  <Fragment key={po.id}>
                    <tr>
                      <td style={{ color: 'var(--text-muted)' }}>{fmt(po.created_at)}</td>
                      <td style={{ fontSize: 13 }}>
                        {po.supplierInfo?.display_name ?? (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td>{po.items.length}</td>
                      <td>
                        {totalCost > 0 ? (
                          AMD(totalCost)
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{po.created_by ?? '—'}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{po.note ?? '—'}</td>
                      <td>
                        <button
                          className="btn-ghost btn-sm"
                          style={{ color: 'var(--danger)' }}
                          disabled={deleting === po.id}
                          onClick={() => setConfirmDelete(po)}
                          title={t('stockIn.undoTitleRow')}
                        >
                          {deleting === po.id ? '…' : <X className="ui-i" />}
                        </button>
                      </td>
                    </tr>
                    {po.items.length > 0 && (
                      <tr key={`${po.id}-items`}>
                        <td colSpan={7} style={{ paddingTop: 0, paddingLeft: 32, paddingBottom: 12 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {po.items.map((item) => (
                              <span
                                key={item.id}
                                style={{
                                  background: 'var(--surface-raised)',
                                  borderRadius: 4,
                                  padding: '2px 8px',
                                  fontSize: 13,
                                  color: 'var(--text-muted)',
                                }}
                              >
                                {item.ingredient_name}: +{item.quantity} {item.unit}
                                {item.unit_cost > 0 ? ` @ ${AMD(item.unit_cost)}` : ''}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {filteredReceivedOrders.length > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px 12px',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {t('common.showing', {
                from: page * PAGE_SIZE + 1,
                to: Math.min((page + 1) * PAGE_SIZE, filteredReceivedOrders.length),
                total: filteredReceivedOrders.length,
              })}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="btn-ghost btn-sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('common.prev')}
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {page + 1} / {historyPageCount}
              </span>
              <button
                className="btn-ghost btn-sm"
                disabled={page + 1 >= historyPageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
